import {Button, Hero} from '@primer/react-brand/esm'
import {clsx} from 'clsx'
import {useEffect, useState, type MouseEventHandler} from 'react'

import {WebGLBrandedDivider} from '@github-ui/brand-webgl'
import type {OS} from '@github-ui/get-os'
import {getAnalyticsEvent} from '@github-ui/swp-core/lib/utils/analytics'

import {InstallerActionMenu, type InstallerItem} from './../InstallerActionMenu/InstallerActionMenu'

import Styles from './CtaBanner.module.css'

export interface CtaBannerProps {
  label: string
  heading: string
  body: string
  initialOS?: OS
  cta1: {
    items: InstallerItem[]
  }
  cta2: {
    label: string
    href: string
  }
  mobileHandoff?: {
    label: string
    onOpen: MouseEventHandler<HTMLButtonElement>
  }
}

export const CtaBanner = ({label, heading, body, cta1, cta2, initialOS, mobileHandoff}: CtaBannerProps) => {
  const [isMounted, setIsMounted] = useState<boolean>(false)

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setIsMounted(true)
  }, [])

  return (
    <div className={clsx(Styles['CtaBanner'], !isMounted && Styles['CtaBanner--hidden'])}>
      <Hero
        className={Styles['CtaBanner__hero']}
        variant="gridline"
        align="center"
        enableAnimation
        aria-label={heading}
        aria-labelledby={undefined}
        trailingComponent={() => (
          <div className={Styles['CtaBanner__ctas']}>
            {mobileHandoff ? (
              <Button
                aria-haspopup="dialog"
                className={Styles['CtaBanner__mobileCta']}
                type="button"
                variant="primary"
                {...getAnalyticsEvent({
                  action: mobileHandoff.label,
                  tag: 'button',
                  context: 'mobile_handoff',
                  location: 'cta_banner',
                })}
                onClick={mobileHandoff.onOpen}
              >
                {mobileHandoff.label}
              </Button>
            ) : (
              <InstallerActionMenu items={cta1.items} initialOS={initialOS} location="cta_banner" />
            )}

            <Button
              as="a"
              href={cta2.href}
              variant="subtle"
              hasArrow={false}
              {...getAnalyticsEvent({action: cta2.label, tag: 'a', context: 'cta', location: 'cta_banner'})}
            >
              {cta2.label}
            </Button>
          </div>
        )}
      >
        <Hero.Label>{label}</Hero.Label>
        <Hero.Heading {...({as: 'h2', id: 'ctaBanner-hero-heading'} as Record<string, unknown>)}>
          {heading}
        </Hero.Heading>

        <Hero.Description>{body}</Hero.Description>
      </Hero>

      <WebGLBrandedDivider
        className={Styles['CtaBanner__brandedDivider']}
        params={{
          character: 'invertocat',
          layout: 1,
        }}
      />
    </div>
  )
}
